// The testing surfaces, driven by the active operating-effectiveness scenario.
//
// A test-shaped basis item is a test: an obtained one has been run, a planned one hasn't. Turning
// each into the shape these pages already render is what makes a scenario demonstrable end to end —
// what the plan asked for and what Testing shows are the same thing, rather than two sets of
// fixtures that happen to resemble each other.
import {useMemo} from "react";
import {BasisItem, DemoControl, Outcome, Scenario, TestParameters, controlOf} from "../../../OeDemo/model";
import {Procedure, generateParameters, procedureById, procedureForArea} from "../../../OeDemo/procedures";
import {testIdFor} from "../../../OeDemo/testLink";
import {useScenario} from "../../../OeDemo/store";
import {TestProgress} from "../../../OeDemo/testLifecycle";
import {FieldType, POPULATIONS, RecordColumn, sampledRow} from "./populationsData";
import {SAMPLES} from "./samplesData";
import {ControlTest, ExtractionField, ItemOutcome, TestCriterion, TestItem, TestStatus, slugKey} from "./testsData";

/**
 * Which of the demo samples a control's items are drawn from. The plan states its population as a
 * sentence, but the item tables need real rows, so each control is pinned to one of the samples that
 * exists. A control with no entry still produces a test — it just has nothing to show per item,
 * which is the honest answer for a one-off configuration observation.
 */
const SAMPLE_FOR_CONTROL: Record<string, string> = {
  "JE-01": "gl-high-value",
  "JE-03": "gl-high-value",
  "JE-07": "gl-high-value",
  "BR-04": "gl-high-value",
  "VI-02": "po-random",
  "VI-04": "po-random",
  "VI-06": "po-random",
  "PAY-02": "joiners-random",
  "PAY-04": "joiners-random",
  "O2C-06": "vendor-random-25",
  "ITGC-01": "access-privileged",
  "ITGC-03": "access-privileged",
};

/**
 * Which procedure a control is tested with, where the area alone doesn't say. Several controls over
 * one population are the normal case — a journal sample carries the authorisation control, the
 * segregation control and the documentation control — and they are not all the same test.
 */
const PROCEDURE_FOR_CONTROL: Record<string, string> = {
  "JE-01": "lib-je-authorisation",
  "JE-03": "lib-segregation",
  "JE-07": "lib-je-authorisation",
  "VI-02": "lib-three-way-match",
  "O2C-06": "lib-vendor-approval",
  "ITGC-01": "lib-access-review",
  "ITGC-03": "lib-access-review",
};

/** The 1–5 display scale, from the four-value outcome the model actually concludes on. */
const EFFECTIVENESS: Partial<Record<Outcome, number>> = {
  operated_effectively: 4,
  operated_with_exceptions: 3,
  did_not_operate_effectively: 1,
};

const FIELD_TYPE: Record<string, FieldType> = {
  number: "number",
  date: "date",
  currency: "currency",
  boolean: "boolean",
};

const fieldType = (type: string): FieldType => FIELD_TYPE[type.toLowerCase()] ?? "text";

/**
 * What the test will be run against. A planned item carries this already; an obtained one predates
 * the plan wizard, so its criteria and fields are rebuilt from the same library generator rather
 * than a second set of rules.
 */
const parametersFor = (
  item: BasisItem,
  subject: DemoControl | undefined,
  procedure: Procedure | undefined,
): TestParameters | undefined => {
  if (item.status === "planned") {
    return item.evidence.kind === "test" ? item.evidence.parameters : undefined;
  }
  if (item.evidence.kind !== "test") return undefined;
  const generated = generateParameters(item.evidence.label, {
    procedure,
    population: subject?.title ?? "",
    frequency: subject?.frequency ?? "monthly",
    singleItem: item.evidence.itemsTested === 1,
  });
  return {...generated, sampleSize: item.evidence.itemsTested};
};

const normalise = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, "");

/** The population column an extraction field means, where the two happen to name the same thing. */
const columnFor = (field: ExtractionField, schema: RecordColumn[]): RecordColumn | undefined =>
  schema.find(c => normalise(c.label) === normalise(field.label) || normalise(c.key) === normalise(field.label));

/** Name pools for people-shaped fields. Offset by two so an approver never equals the preparer. */
const PEOPLE = ["S. Patel", "R. Mehta", "J. Okonkwo", "L. Fischer", "A. Novak", "T. Eriksen", "M. Lindqvist"];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** A date spread across the period, deterministic per item so the table is stable. */
const spreadDate = (index: number): string =>
  `${String(((index * 7) % 27) + 1).padStart(2, "0")} ${MONTHS[(index * 5) % 12]} 2026`;

/**
 * A stand-in for a field the fixture population doesn't carry, so an extracted column reads as data
 * rather than as a hole. Demo shim — real extraction reads the evidence. Values are shaped by the
 * field's label so the table reads as plausible records, and paired date fields (approval/posting)
 * land on the same day so date-order criteria hold.
 */
const placeholder = (field: ExtractionField, index: number): string => {
  const label = normalise(field.label);
  if (label.includes("preparer") || label.includes("requester")) return PEOPLE[index % PEOPLE.length];
  if (label.includes("approver") || label.includes("reviewer")) return PEOPLE[(index + 2) % PEOPLE.length];
  switch (field.type) {
    case "date":
      return spreadDate(index);
    case "number":
      return String((index % 9) + 1);
    case "currency":
      return `£${(((index * 37) % 90) + 5) * 100}`;
    case "boolean":
      return index % 4 === 0 ? "No" : "Yes";
    default:
      return `${field.label} ${index + 1}`;
  }
};

/**
 * One row per item the test covers. Results are laid out so that exactly the exception count fails,
 * which is what keeps the numbers on the OE card and the numbers here the same by construction.
 */
const buildItems = (
  count: number,
  schema: RecordColumn[],
  rows: Record<string, string>[],
  fields: ExtractionField[],
  criteria: TestCriterion[],
  exceptions: number,
  /** How many of the items have been worked. A part-run test leaves the rest untouched. */
  tested: number,
): TestItem[] =>
  Array.from({length: count}, (_, i) => {
    const row = sampledRow(rows, schema, i);
    const run = i < tested;
    const failed = run && i < exceptions;
    // An exception's justification says the failing criterion could not be evidenced, so the field
    // that criterion reads must be absent from the extraction — data and verdict have to agree.
    const failingCriterion = normalise(criteria[0]?.name ?? "");
    const isMissing = (f: ExtractionField) => failed && failingCriterion.includes(normalise(f.label).split(" ")[0]);
    return {
      evidenceCount: run ? 1 : 0,
      // Nothing is extracted or judged until an item has been worked; its tabs read as not started.
      extracted: run
        ? Object.fromEntries(
            fields.map(f => {
              if (isMissing(f)) return [f.key, "—"];
              const column = columnFor(f, schema);
              return [f.key, (column && row[column.key]) || placeholder(f, i)];
            }),
          )
        : {},
      results: run
        ? Object.fromEntries(
            criteria.map((c, n) => [
              c.key,
              {
                // One failing criterion is what makes an item an exception; the rest still pass.
                outcome: (failed && n === 0 ? "Fail" : "Pass") as ItemOutcome,
                justification: failed && n === 0 ? `${c.name} could not be evidenced for this item.` : undefined,
              },
            ]),
          )
        : {},
    };
  });

const testFrom = (
  scenario: Scenario,
  control: string,
  scope: string,
  item: BasisItem,
  progress: TestProgress | undefined,
): ControlTest | undefined => {
  if (item.evidence.kind !== "test") return undefined;

  const id = testIdFor(scenario.id, control, scope, item.id);
  const subject = controlOf(scenario, control);
  // A planned item names the procedure it was characterised from; an obtained one predates the
  // wizard, so the library is asked which procedure covers its area.
  const procedure =
    item.status === "planned" && item.evidence.kind === "test"
      ? procedureById(item.evidence.procedure?.id ?? "")
      : (procedureById(PROCEDURE_FOR_CONTROL[control] ?? "") ?? procedureForArea(subject?.assessment));
  const parameters = parametersFor(item, subject, procedure);
  const sample = SAMPLES.find(s => s.id === SAMPLE_FOR_CONTROL[control]);
  const population = sample ? POPULATIONS.find(p => p.id === sample.populationId) : undefined;

  const criteria: TestCriterion[] = (parameters?.criteria ?? []).map(c => ({
    key: slugKey(c.name),
    name: c.name,
    guidance: c.guidance,
  }));
  const extractionFields: ExtractionField[] = (parameters?.fields ?? []).map(f => ({
    key: slugKey(f.name),
    label: f.name,
    type: fieldType(f.type),
    guidance: f.guidance,
  }));

  const obtained = item.status === "obtained";
  const sampleSize = parameters?.sampleSize ?? 0;
  // A concluded test has worked its whole sample; an unconcluded one has got as far as the engine says.
  const tested = obtained ? sampleSize : (progress?.tested ?? 0);
  const exceptions = obtained && item.evidence.kind === "test" ? item.evidence.exceptions : (progress?.exceptions ?? 0);
  const outcome = obtained && item.evidence.kind === "test" ? item.evidence.outcome : undefined;

  return {
    id,
    name:
      item.status === "obtained"
        ? item.evidence.label
        : `${control} — ${item.evidence.kind === "test" ? (item.evidence.procedure?.name ?? "test") : "test"}`,
    description:
      item.status === "obtained"
        ? (item.evidence.conclusion ?? "")
        : item.evidence.kind === "test"
          ? item.evidence.goal
          : "",
    control: `${control} — ${subject?.title ?? ""}`,
    controls: [{ref: control, name: subject?.title ?? control}],
    baseProcedureId: procedure?.id ?? "",
    sampleId: sample?.id ?? "",
    sampleName: sample?.name ?? parameters?.population ?? "",
    status: (obtained ? "Complete" : progress ? "In progress" : "Not started") as TestStatus,
    effectivenessScore: outcome ? EFFECTIVENESS[outcome] : undefined,
    effectivenessJustification: obtained && item.evidence.kind === "test" ? item.evidence.conclusion : undefined,
    extractionFields,
    criteria,
    items: buildItems(
      sampleSize,
      population?.schema ?? [],
      population?.rows ?? [],
      extractionFields,
      criteria,
      exceptions,
      tested,
    ),
    unmatchedEvidence: obtained ? sample?.unmatchedEvidence : undefined,
  };
};

/** Every test the active scenario implies, with any plan created in the wizard layered over it. */
export const useScenarioTests = (): ControlTest[] => {
  const scenario = useScenario();

  return useMemo(
    () =>
      scenario.oe.flatMap(assessment =>
        assessment.basis
          .map(item => testFrom(scenario, assessment.control, assessment.scope, item, assessment.underway?.[item.id]))
          .filter((test): test is ControlTest => test !== undefined),
      ),
    [scenario],
  );
};

export const useScenarioTest = (testId: string): ControlTest | undefined =>
  useScenarioTests().find(t => t.id === testId);
